import { useCallback, useEffect, useRef, useState } from 'react';
import { FilterSessionStorageKey } from '../constants/storageKey';
import { getShortUrl } from '../utils/shortUrl';
import { IDefaultValueProps } from '../types';
import { cloneDeep } from 'lodash-es';
import { sortConfigByDependencyOrder } from '../utils';

export default function useDefaultValue({
  userDefaultValue = {},
  shortid,
  finallyDealwithDefaultValue = value => value,
  renderConfig,
  condLoading,
  instanceId = 'default',
  initStore,
  dependencyOrder,
  onShortIdLoaded,
}: IDefaultValueProps & { initStore: (value: any) => Promise<void>; onShortIdLoaded?: (extraData: Record<string, any>) => void }) {
  function getDefaultValue(defaultFilterValue: any = {}) {
    // 按 dependencyOrder 排序后的配置
    const sortedConfig = sortConfigByDependencyOrder(renderConfig, dependencyOrder);
    // 不能直接将{} 替换为defaultFilterValue，我们只取需要的参数
    const filterValue = sortedConfig.reduce((pre, item) => {
      const { props, defaultValue } = item;
      const data = defaultValue?.(pre, props as any);
      return { ...pre, ...data };
    }, defaultFilterValue);
    return filterValue;
  }
  const [loading, setLoading] = useState(true);
  const [defaultValue, setDefaultValue] = useState({});

  const originalDefaultValue = useRef({});

  const sessionStorageParams = useCallback(() => {
    const data = JSON.parse(
      sessionStorage.getItem(`${FilterSessionStorageKey}_${instanceId}`) ||
        '{}',
    );
    return data;
  }, []);

  function justifyHasValue(value: any) {
    if (!value) {
      return false;
    }

    if (Array.isArray(value)) {
      return false;
    }

    if (typeof value === 'object') {
      return Object.keys(value).length > 0;
    }

    return false;
  }

  /**
   * 从三个位置获取默认值
   * 本地缓存
   * 用户默认值
   * 服务器短链接缓存
   *
   * 优先级: 本地缓存 < 用户默认值 < 服务器短链接缓存
   * @returns
   */
  const getDefaultFilterInfo = async () => {
    // 服务器短链接缓存默认值
    const shortServerFilter = await getShortUrl(shortid, defaultValue);

    if (justifyHasValue(shortServerFilter)) {
      onShortIdLoaded?.(shortServerFilter);
      return shortServerFilter;
    }

    if (justifyHasValue(userDefaultValue)) {
      return userDefaultValue;
    }

    // 本地缓存默认值
    const data = sessionStorageParams();

    if (justifyHasValue(data)) {
      return data;
    }

    return {};
  };

  /**
   * 初始化默认值
   */
  async function initDefaultValue() {
    setLoading(true);
    const defaultValue = await getDefaultFilterInfo();
    const value = getDefaultValue(defaultValue);
    const realFilterValue = await finallyDealwithDefaultValue(value);

    setDefaultValue(realFilterValue);
    await initStore(realFilterValue);
    setTimeout(() => setLoading(false), 100);
  }

  useEffect(() => {
    if (!condLoading) {
      originalDefaultValue.current = getDefaultValue(
        cloneDeep(userDefaultValue),
      );

      initDefaultValue();
    }
  }, [condLoading]);

  return {
    loading,
    defaultValue,
    originalDefaultValue,
  };
}
